import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Layers,
  Search,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';

import { humanizeLabel } from '@/nucleo/displayLabels';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid } from '@/ui/StatsGrid';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import { DADOS_AUDITORIA } from './dados';

/**
 * `/operacao/financeiro/auditoria` — trilha imutável de operações
 * financeiras críticas (fechamento mensal, demonstrativo, lote SOF, prazo
 * legal), com hash SHA-256 encadeado: o hash de cada evento incorpora o hash
 * do evento anterior, então qualquer alteração num registro antigo quebra a
 * cadeia a partir dali.
 *
 * A trilha em si é somente-inserção no banco (CLAUDE.md, "Regras de banco",
 * item 10); esta tela só lê. "Verificar integridade" aqui é decorativo — não
 * há recálculo de hash de verdade, porque não há API nem banco ainda (ver
 * `../_dev/cenarioDeTela.ts`).
 *
 * Dado simulado por decisão de produto, lido de `dados.ts` (que monta as datas
 * relativas a hoje). Troque `DADOS_AUDITORIA` e o `EstadoSimulado` pela
 * leitura da API, no mesmo `dados.ts` (padrão de
 * `modulos/admin/integracoes/dados.ts`) quando o módulo nascer na API — nesse
 * momento a verificação de integridade também passa a chamar a API de verdade.
 */

interface EventoAuditoria {
  id: string;
  sequencia: number;
  registradoEm: string;
  evento: string;
  categoria: string;
  ator: string | null;
  entidadeTipo: string;
  entidadeId: string;
  hashAnterior: string | null;
  hashAtual: string;
  payload: Record<string, unknown>;
}

interface KpisAuditoria {
  totalEventos: number;
  ultimas24h: number;
  atoresUnicos: number;
  categorias: number;
  tiposEvento: number;
}

interface DadosAuditoria {
  kpis: KpisAuditoria;
  eventos: EventoAuditoria[];
}

const DADOS = DADOS_AUDITORIA as DadosAuditoria;

const CATEGORIAS = ['FECHAMENTO', 'DEMONSTRATIVO', 'LOTE_SOF', 'PRAZO_LEGAL'];

const CAMPOS_FILTRO: FilterFieldConfig[] = [
  {
    key: 'categoria',
    label: 'Categoria',
    type: 'select',
    options: CATEGORIAS.map((categoria) => ({ value: categoria, label: humanizeLabel(categoria) })),
  },
  { key: 'evento', label: 'Evento (exato)', type: 'search', placeholder: 'ex: FECHAMENTO_CRIADO' },
  { key: 'ator', label: 'Ator', type: 'search', placeholder: 'Nome do usuário' },
];

function fmtDataHora(iso: string): string {
  return format(new Date(iso), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR });
}

const KPIS_VAZIOS: KpisAuditoria = {
  totalEventos: 0,
  ultimas24h: 0,
  atoresUnicos: 0,
  categorias: 0,
  tiposEvento: 0,
};

const PADRAO_POR_PAGINA = 10;

export function AuditoriaFinanceiraPage() {
  const cenario = useCenarioDeTela();
  const eventos = useMemo(() => (cenario === 'sucesso' ? DADOS.eventos : []), [cenario]);
  const kpis = cenario === 'sucesso' ? DADOS.kpis : KPIS_VAZIOS;

  const [filtro, setFiltro] = useState<Record<string, string>>({});
  const [periodo, setPeriodo] = useState<DateRangeValue>({ preset: 'ALL' });
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);
  const [detalhe, setDetalhe] = useState<EventoAuditoria | null>(null);
  const [verificando, setVerificando] = useState(false);
  const [resultadoVerificacao, setResultadoVerificacao] = useState<{
    totalVerificado: number;
  } | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const aplicarFiltro = (chave: string, valor: string) => {
    setPagina(1);
    setFiltro((atual) => ({ ...atual, [chave]: valor }));
  };

  const limparFiltro = () => {
    setPagina(1);
    setFiltro({});
    setPeriodo({ preset: 'ALL' });
  };

  const eventosFiltrados = useMemo(() => {
    const { from, to } = resolveDateRange(periodo);
    return eventos.filter((ev) => {
      if (filtro.categoria && ev.categoria !== filtro.categoria) return false;
      if (filtro.evento && ev.evento !== filtro.evento.toUpperCase()) return false;
      if (filtro.ator && !(ev.ator ?? 'Sistema').toLowerCase().includes(filtro.ator.toLowerCase()))
        return false;
      if (from && ev.registradoEm.slice(0, 10) < from) return false;
      if (to && ev.registradoEm.slice(0, 10) > to) return false;
      return true;
    });
  }, [eventos, filtro, periodo]);

  const totalFiltrado = eventosFiltrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalFiltrado / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const linhasPagina = eventosFiltrados.slice(
    (paginaAtual - 1) * porPagina,
    paginaAtual * porPagina,
  );

  const handleVerificarIntegridade = () => {
    setVerificando(true);
    setResultadoVerificacao(null);
    timeoutRef.current = window.setTimeout(() => {
      setVerificando(false);
      setResultadoVerificacao({ totalVerificado: kpis.totalEventos });
    }, 700);
  };

  const COLUNAS: DataTableColumn<EventoAuditoria>[] = [
    {
      header: '#',
      headerTermo: 'SEQUENCIA_AUDIT',
      accessorKey: 'sequencia',
      cellClassName: 'font-mono text-xs',
    },
    {
      header: 'Quando',
      cell: (ev) => <span className="text-xs">{fmtDataHora(ev.registradoEm)}</span>,
    },
    {
      header: 'Evento',
      cell: (ev) => <Badge variant="outline">{humanizeLabel(ev.evento)}</Badge>,
    },
    {
      header: 'Categoria',
      headerTermo: 'CATEGORIA_AUDIT',
      cell: (ev) => <Badge variant="secondary">{humanizeLabel(ev.categoria)}</Badge>,
    },
    {
      header: 'Ator',
      cell: (ev) => <span className="text-sm">{ev.ator ?? 'Sistema'}</span>,
    },
    {
      header: 'Hash',
      headerTermo: 'HASH_ENCADEADO',
      cell: (ev) => (
        <span className="font-mono text-xs text-muted-foreground">
          {ev.hashAtual.slice(0, 12)}…
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Auditoria Financeira"
        description={
          <>
            Trilha imutável append-only com{' '}
            <GlossaryTerm termo="HASH_ENCADEADO">hash SHA-256 encadeado</GlossaryTerm> de todas as
            operações financeiras críticas (fechamentos, demonstrativos, lotes SOF e prazos legais).
          </>
        }
      >
        <Button
          variant="outline"
          onClick={handleVerificarIntegridade}
          disabled={verificando || cenario !== 'sucesso'}
        >
          <ShieldCheck className="mr-2 h-4 w-4" />
          {verificando ? 'Verificando…' : 'Verificar integridade'}
        </Button>
      </PageHeader>

      {resultadoVerificacao && (
        <div role="status" aria-live="polite">
          <Alert className="border-success/50 bg-success/10 text-success">
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
            <AlertDescription className="text-success">
              <div className="flex items-start justify-between gap-3">
                <span>
                  Cadeia íntegra: {resultadoVerificacao.totalVerificado} registros verificados,
                  nenhuma adulteração encontrada.
                </span>
                <button
                  type="button"
                  onClick={() => setResultadoVerificacao(null)}
                  aria-label="Fechar aviso"
                  className="shrink-0 rounded-sm text-current opacity-70 hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}

      <StatsGrid
        columns="grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
        stats={[
          { title: 'Total de eventos', value: kpis.totalEventos, icon: Activity },
          { title: 'Últimas 24h', value: kpis.ultimas24h, icon: ShieldCheck },
          { title: 'Atores únicos', value: kpis.atoresUnicos, icon: Users },
          { title: 'Categorias', value: kpis.categorias, icon: Layers },
          { title: 'Tipos de evento', value: kpis.tiposEvento, icon: AlertTriangle },
        ]}
      />

      <FilterBar
        fields={CAMPOS_FILTRO}
        values={filtro}
        onChange={aplicarFiltro}
        onClear={limparFiltro}
      >
        <DateRangePresetFilter
          label="Período"
          value={periodo}
          onChange={(proximo) => {
            setPagina(1);
            setPeriodo(proximo);
          }}
        />
      </FilterBar>

      <EstadoSimulado
        cenario={cenario}
        descricao="a auditoria financeira"
        colunasDoEsqueleto={COLUNAS.length}
      >
        <DataTable
          columns={COLUNAS}
          data={linhasPagina}
          total={totalFiltrado}
          page={paginaAtual}
          perPage={porPagina}
          onPageChange={setPagina}
          onPerPageChange={(novoValor) => {
            setPorPagina(novoValor);
            setPagina(1);
          }}
          onRowClick={(ev) => setDetalhe(ev)}
          emptyIcon={Search}
          emptyTitle="Nenhum evento de auditoria"
          emptyDescription="Ajuste os filtros ou aguarde novas operações financeiras."
          ariaLabel="Trilha de auditoria financeira"
        />
      </EstadoSimulado>

      <Dialog
        open={!!detalhe}
        onOpenChange={(aberto) => {
          if (!aberto) setDetalhe(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Evento #{detalhe?.sequencia} — {detalhe ? humanizeLabel(detalhe.evento) : ''}
            </DialogTitle>
            <DialogDescription>
              Detalhe somente leitura do evento na trilha de auditoria.
            </DialogDescription>
          </DialogHeader>
          {detalhe && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Categoria</p>
                  <p className="font-medium">{humanizeLabel(detalhe.categoria)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Quando</p>
                  <p className="font-medium">{fmtDataHora(detalhe.registradoEm)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ator</p>
                  <p className="font-medium">{detalhe.ator ?? 'Sistema'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Entidade</p>
                  <p className="font-medium">
                    {humanizeLabel(detalhe.entidadeTipo)} / {detalhe.entidadeId}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Hash anterior</p>
                <p className="font-mono text-xs break-all">{detalhe.hashAnterior ?? 'GENESIS'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Hash atual</p>
                <p className="font-mono text-xs break-all">{detalhe.hashAtual}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Payload</p>
                <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-60">
                  {JSON.stringify(detalhe.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AuditoriaFinanceiraPage;
